import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router";
import { Copy, Loader2, Plus, Search } from "lucide-react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { useGfsAssessmentListQuery } from "@/services/backend/gfs/assessment/api";
import { useGfsCatalogListQuery } from "@/services/backend/gfs/catalog/api";
import { preferredGfsCatalogId } from "@/services/backend/gfs/catalog/catalog-selection";
import { GfsAssessmentListItem } from "@/services/backend/gfs/assessment/types";
import {
  useGfsCalculationCreateFromAssessmentMutation,
  useGfsCalculationCreateFromOldMutation,
  useGfsCalculationCreateMutation,
  useGfsCalculationListQuery,
} from "@/services/backend/gfs/calculation/api";
import { GfsCalculationListItem } from "@/services/backend/gfs/calculation/types";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  MilestonePage,
  MilestonePageActions,
  MilestonePageContent,
  MilestonePageHeader,
  MilestonePageTitle,
} from "@/shared/bsm/core/milestone/components/ui/milestone-page";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { toast } from "sonner";

export function GfsCalculationListPage() {
  const { processId = "" } = useParams();
  const { data: process, error: processError } = useProcessesProcessShowQuery({
    id: processId,
  });

  if (processError) return <RTKQueryErrorAlert error={processError} />;
  if (!process) return null;
  if (!process.customerId) {
    return <div>{t("Diesem Vorgang ist noch kein Kunde zugeordnet.")}</div>;
  }

  return (
    <CalculationPage
      companyId={process.companyId}
      customerId={process.customerId}
    />
  );
}

function CalculationPage({
  companyId,
  customerId,
}: {
  companyId: string;
  customerId: string;
}) {
  const {
    data: calculations,
    error: calculationError,
    isLoading: calculationsLoading,
  } = useGfsCalculationListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
  });
  const {
    data: assessments,
    error: assessmentError,
    isLoading: assessmentsLoading,
  } = useGfsAssessmentListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
  });

  return (
    <MilestonePage>
      <MilestonePageHeader>
        <MilestonePageTitle>{t("Kalkulationen")}</MilestonePageTitle>
        <MilestonePageActions>
          <CreateCalculationDialog
            assessments={assessments?.items ?? []}
            assessmentsLoading={assessmentsLoading}
            calculations={calculations?.items ?? []}
            companyId={companyId}
            customerId={customerId}
          />
        </MilestonePageActions>
      </MilestonePageHeader>
      <MilestonePageContent>
        {calculationError && <RTKQueryErrorAlert error={calculationError} />}
        {assessmentError && <RTKQueryErrorAlert error={assessmentError} />}
        <Card>
          <CalculationHistoryTable
            isLoading={calculationsLoading}
            items={calculations?.items ?? []}
          />
        </Card>
      </MilestonePageContent>
    </MilestonePage>
  );
}

function CreateCalculationDialog({
  assessments,
  assessmentsLoading,
  calculations,
  companyId,
  customerId,
}: {
  assessments: GfsAssessmentListItem[];
  assessmentsLoading: boolean;
  calculations: GfsCalculationListItem[];
  companyId: string;
  customerId: string;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const closeDialogAndNavigate = useCallback(
    (calculationId: string) => {
      setOpen(false);
      navigate(calculationId);
    },
    [navigate],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-5 w-5" />
          <span>{t("Kalkulation")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>{t("Neue Kalkulation erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Kalkulationen können komplett neu oder auf Basis vorhandener Daten erstellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="manual">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">{t("Neu Anlegen")}</TabsTrigger>
            <TabsTrigger value="from-assessment">
              {t("Bestandsaufnahmen")}
            </TabsTrigger>
            <TabsTrigger value="from-old">
              {t("Alte Kalkulationen")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <CreateManually
              companyId={companyId}
              customerId={customerId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-assessment">
            <CreateFromAssessment
              assessments={assessments}
              assessmentsLoading={assessmentsLoading}
              companyId={companyId}
              customerId={customerId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-old">
            <CreateFromOld
              calculations={calculations}
              companyId={companyId}
              customerId={customerId}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateManually({
  companyId,
  customerId,
  onCreated,
}: {
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  const [create, { isLoading, error }] = useGfsCalculationCreateMutation();
  const { data: catalogs } = useGfsCatalogListQuery({
    company: { active: true, values: [companyId] },
    limit: 100,
  });
  const [catalogId, setCatalogId] = useState("");
  const availableCatalogs = (catalogs?.items ?? []).filter(
    (catalog) => !catalog.customerId || catalog.customerId === customerId,
  );

  useEffect(() => {
    if (catalogId) return;
    const preferredCatalogId = preferredGfsCatalogId(
      availableCatalogs,
      customerId,
    );
    if (preferredCatalogId) setCatalogId(preferredCatalogId);
  }, [availableCatalogs, catalogId, customerId]);

  const onClick = () => {
    if (isLoading) return;
    const id = v4();
    create({ id, companyId, customerId, catalogId })
      .unwrap()
      .then(() => onCreated(id));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Neu Anlegen")}</CardTitle>
        <CardDescription>
          {t(
            "Erstellt eine leere Kalkulation mit den Basisdaten des Kunden. Die Bestandsaufnahme kann anschließend gewählt werden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          <label className="text-sm font-medium">{t("Katalog")}</label>
          <Select value={catalogId} onValueChange={setCatalogId}>
            <SelectTrigger>
              <SelectValue placeholder={t("Katalog auswählen")} />
            </SelectTrigger>
            <SelectContent>
              {availableCatalogs.map((catalog) => (
                <SelectItem key={catalog.id} value={catalog.id}>
                  {catalog.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <RTKQueryErrorAlert error={error} />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={onClick} disabled={isLoading || !catalogId}>
          {t("Erstellen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateFromAssessment({
  assessments,
  assessmentsLoading,
  companyId,
  customerId,
  onCreated,
}: {
  assessments: GfsAssessmentListItem[];
  assessmentsLoading: boolean;
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("Daten auf Basis einer Bestandsaufnahme laden")}
        </CardTitle>
        <CardDescription>
          {t(
            "Erstellt eine Kalkulation und kopiert die Daten der gewählten Bestandsaufnahme.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AssessmentsTable
          assessments={assessments}
          assessmentsLoading={assessmentsLoading}
          companyId={companyId}
          customerId={customerId}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function AssessmentsTable({
  assessments,
  assessmentsLoading,
  companyId,
  customerId,
  onCreated,
}: {
  assessments: GfsAssessmentListItem[];
  assessmentsLoading: boolean;
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  if (assessmentsLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead className="w-56">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...assessments].sort(byCreatedAtAndId).map((assessment) => (
          <TableRow key={assessment.id}>
            <TableCell>
              <DocumentNumberById
                documentId={assessment.id}
                skip={!assessment.immutable}
              />
            </TableCell>
            <TableCell>{formatDate(assessment.createdAt)}</TableCell>
            <TableCell>{assessment.subject || t("Ohne Betreff")}</TableCell>
            <TableCell>
              <CreateCalculationFromAssessmentButton
                assessmentId={assessment.id}
                companyId={companyId}
                customerId={customerId}
                onCreated={onCreated}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CreateCalculationFromAssessmentButton({
  assessmentId,
  companyId,
  customerId,
  onCreated,
}: {
  assessmentId: string;
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  const [create, { isLoading, error }] =
    useGfsCalculationCreateFromAssessmentMutation();

  const onClick = () => {
    if (isLoading) return;
    const id = v4();
    create({ id, companyId, customerId, assessmentId })
      .unwrap()
      .then(() => onCreated(id))
      .catch(() => {
        toast.error(t("Fehler"), {
          description: t("Die Kalkulation konnte nicht erstellt werden."),
        });
      });
  };

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button onClick={onClick} disabled={isLoading} size="sm">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && (
          <>
            <Copy className="mr-3 h-4 w-4" />
            <span>{t("Daten Übernehmen")}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function CreateFromOld({
  calculations,
  companyId,
  customerId,
  onCreated,
}: {
  calculations: GfsCalculationListItem[];
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Kalkulation")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten der alten Kalkulation. Es werden Kalkulationen dieses Kunden angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <OldCalculationsTable
          calculations={calculations}
          companyId={companyId}
          customerId={customerId}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function OldCalculationsTable({
  calculations,
  companyId,
  customerId,
  onCreated,
}: {
  calculations: GfsCalculationListItem[];
  companyId: string;
  customerId: string;
  onCreated: (calculationId: string) => void;
}) {
  const sortedItems = useMemo(
    () => [...calculations].sort(byCreatedAtAndId),
    [calculations],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>{formatDate(calculation.createdAt)}</TableCell>
            <TableCell>
              <DocumentNumberById
                documentId={calculation.id}
                skip={!calculation.immutable}
              />
            </TableCell>
            <TableCell>
              {calculation.assessmentSubject || t("Nicht gewählt")}
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink to={calculation.id}>
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
                <CopyCalculationButton
                  calculationId={calculation.id}
                  companyId={companyId}
                  customerId={customerId}
                  onCopied={onCreated}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CopyCalculationButton({
  calculationId,
  companyId,
  customerId,
  onCopied,
}: {
  calculationId: string;
  companyId: string;
  customerId: string;
  onCopied: (calculationId: string) => void;
}) {
  const [copy, { isLoading, error }] = useGfsCalculationCreateFromOldMutation();

  const onClick = () => {
    if (isLoading) return;
    const newCalculationId = v4();
    copy({
      oldCalculationId: calculationId,
      newCalculationId,
      companyId,
      customerId,
    })
      .unwrap()
      .then(() => onCopied(newCalculationId))
      .catch(() => {
        toast.error(t("Fehler"), {
          description: t("Die Kalkulation konnte nicht kopiert werden."),
        });
      });
  };

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button onClick={onClick} disabled={isLoading} size="sm">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && (
          <>
            <Copy className="mr-3 h-4 w-4" />
            <span>{t("Kopieren")}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function CalculationHistoryTable({
  isLoading,
  items,
}: {
  isLoading: boolean;
  items: GfsCalculationListItem[];
}) {
  if (isLoading) {
    return <CalculationHistoryTableSkeleton />;
  }

  const sortedItems = [...items].sort(byCreatedAtAndId);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedItems.map((calculation) => (
          <TableRow key={calculation.id}>
            <TableCell>{formatDate(calculation.createdAt)}</TableCell>
            <TableCell>{formatTime(calculation.createdAt)}</TableCell>
            <TableCell>
              <NavLink to={`${calculation.id}`}>
                <DocumentNumberById
                  documentId={calculation.id}
                  skip={!calculation.immutable}
                />
              </NavLink>
            </TableCell>
            <TableCell>
              {calculation.assessmentSubject || t("Nicht gewählt")}
            </TableCell>
            <TableCell className="text-right">
              {formatCurrency(calculation.pricingTotal)}
            </TableCell>
            <TableCell>
              <NavLink to={`${calculation.id}`}>
                <Button variant="ghost">
                  <Search className="h-4 w-4" />
                </Button>
              </NavLink>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              {t("Keine Kalkulationen gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function CalculationHistoryTableSkeleton() {
  return (
    <Table className="animate-pulse">
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Bestandsaufnahme")}</TableHead>
          <TableHead className="text-right">{t("Summe")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
    </Table>
  );
}

function byCreatedAtAndId(
  left: { createdAt: string; id: string },
  right: { createdAt: string; id: string },
) {
  const createdAtOrder = right.createdAt.localeCompare(left.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return right.id.localeCompare(left.id);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);
}
